import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiSearch,
  FiRefreshCw,
  FiChevronDown,
  FiLock,
  FiArrowLeft,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import TmisLayout from "../components/TmisLayout";
import "../App.css";

const API_BASE = process.env.REACT_APP_API || "http://localhost:3023/api";

const API_ENDPOINTS = {
  getAuditLogs: `${API_BASE}/getAuditLogs`,
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const getAuthToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

const getAxiosConfig = () => {
  const token = getAuthToken();
  if (!token) return {};
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const getLoginUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("Failed to read login user:", error);
    return null;
  }
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";

  try {
    return new Date(dateValue).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const getSortValue = (row, key) => {
  if (key === "created_at") {
    return row.created_at ? new Date(row.created_at).getTime() : 0;
  }

  if (key === "record_id") {
    return Number(row.record_id || 0);
  }

  return String(row[key] || "").toLowerCase();
};

const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <TmisLayout showSidebar={false}>
      <main className="unauthorized-main">
        <div className="unauthorized-card">
          <div className="lock-animation">
            <div className="lock-circle">
              <FiLock />
            </div>
            <span className="pulse-ring"></span>
            <span className="pulse-ring second"></span>
          </div>

          <h1>Access Denied</h1>
          <p>
            You are not authorized to view the Audit Logs page. Please contact
            the administrator if you need access.
          </p>

          <button
            type="button"
            className="unauthorized-btn"
            onClick={() => navigate("/dashboard")}
          >
            <FiArrowLeft />
            Back to Dashboard
          </button>
        </div>
      </main>
    </TmisLayout>
  );
};

const AuditLogs = () => {
  const loginUser = useMemo(() => getLoginUser(), []);
  const role = String(loginUser?.role || "").toLowerCase();

  if (role === "trainer") {
    return <UnauthorizedPage />;
  }

  return <AuditLogsContent />;
};

const AuditLogsContent = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    setPageMessage("");

    try {
      const response = await axios.get(
        API_ENDPOINTS.getAuditLogs,
        getAxiosConfig()
      );

      setAuditLogs(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Load audit logs error:", error);
      setPageMessage(
        error.response?.data?.message || "Failed to load audit logs."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedAction, selectedModule, pageSize]);

  const actionOptions = useMemo(() => {
    const values = auditLogs
      .map((item) => item.action)
      .filter(Boolean)
      .map((item) => String(item));

    return [...new Set(values)].sort();
  }, [auditLogs]);

  const moduleOptions = useMemo(() => {
    const values = auditLogs
      .map((item) => item.module)
      .filter(Boolean)
      .map((item) => String(item));

    return [...new Set(values)].sort();
  }, [auditLogs]);

  const filteredLogs = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return auditLogs.filter((item) => {
      const action = String(item.action || "").toLowerCase();
      const moduleName = String(item.module || "").toLowerCase();
      const description = String(item.description || "").toLowerCase();
      const createdByName = String(
        item.created_by_name || item.name || item.created_by || ""
      ).toLowerCase();
      const recordId = String(item.record_id || "").toLowerCase();

      const matchKeyword =
        !keyword ||
        action.includes(keyword) ||
        moduleName.includes(keyword) ||
        description.includes(keyword) ||
        createdByName.includes(keyword) ||
        recordId.includes(keyword);

      const matchAction =
        !selectedAction || String(item.action || "") === selectedAction;

      const matchModule =
        !selectedModule || String(item.module || "") === selectedModule;

      return matchKeyword && matchAction && matchModule;
    });
  }, [auditLogs, searchText, selectedAction, selectedModule]);

  const sortedLogs = useMemo(() => {
    const rows = [...filteredLogs];

    rows.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

      return 0;
    });

    return rows;
  }, [filteredLogs, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedLogs.length / pageSize));

  const paginatedLogs = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;

    return sortedLogs.slice(start, start + pageSize);
  }, [sortedLogs, currentPage, totalPages, pageSize]);

  const paginationStart =
    sortedLogs.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const paginationEnd = Math.min(currentPage * pageSize, sortedLogs.length);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: "asc",
      };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const getActionClass = (action) => {
    const value = String(action || "").toLowerCase();

    if (value === "login") return "class-code-badge";
    if (value === "create") return "status-badge success";
    if (value === "update") return "status-badge warning";
    if (value === "delete") return "status-badge danger";

    return "class-code-badge";
  };

  return (
    <TmisLayout
      searchText={searchText}
      onSearchChange={setSearchText}
      searchPlaceholder="Search audit logs..."
      showSidebar={false}
    >
      <div className="performance-page-content">
        <section className="performance-card performance-filter-card accessible-card">
          <div className="performance-title-row">
            <div>
              <h1>Audit Logs</h1>
              <p>
                Track login, create, update and delete activities in the system.
              </p>
            </div>

            <button
              type="button"
              className="performance-search-btn"
              onClick={loadAuditLogs}
              disabled={loading}
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>

          {pageMessage && (
            <div className="modal-message warning">{pageMessage}</div>
          )}

          <div className="performance-filter-row one-row-filter">
            <label className="performance-select-group">
              <span>Action</span>
              <div className="performance-select-wrapper">
                <select
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All Actions</option>

                  {actionOptions.map((action) => (
                    <option key={action} value={action}>
                      {action}
                    </option>
                  ))}
                </select>
                <FiChevronDown />
              </div>
            </label>

            <label className="performance-select-group">
              <span>Module</span>
              <div className="performance-select-wrapper">
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All Modules</option>

                  {moduleOptions.map((moduleName) => (
                    <option key={moduleName} value={moduleName}>
                      {moduleName}
                    </option>
                  ))}
                </select>
                <FiChevronDown />
              </div>
            </label>

            <button
              type="button"
              className="performance-search-btn"
              onClick={() => setCurrentPage(1)}
              disabled={loading}
            >
              <FiSearch /> Search
            </button>
          </div>
        </section>

        <section className="performance-card performance-table-card accessible-card">
          <div className="performance-card-header">
            <div>
              <h2>System Activity</h2>
              <p>
                {loading
                  ? "Loading audit logs..."
                  : `${sortedLogs.length} audit log(s) found`}
              </p>
            </div>

            <div className="performance-page-size">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="performance-table-responsive">
            <table className="performance-form-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" onClick={() => handleSort("created_at")}>
                      Date / Time {getSortIcon("created_at")}
                    </button>
                  </th>

                  <th>
                    <button type="button" onClick={() => handleSort("action")}>
                      Action {getSortIcon("action")}
                    </button>
                  </th>

                  <th>
                    <button type="button" onClick={() => handleSort("module")}>
                      Module {getSortIcon("module")}
                    </button>
                  </th>

                  <th>
                    <button type="button" onClick={() => handleSort("record_id")}>
                      Record ID {getSortIcon("record_id")}
                    </button>
                  </th>

                  <th>Description</th>

                  <th>
                    <button
                      type="button"
                      onClick={() => handleSort("created_by_name")}
                    >
                      User {getSortIcon("created_by_name")}
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="performance-empty-message">
                      {loading ? "Loading..." : "No audit logs found."}
                    </td>
                  </tr>
                ) : (
                  paginatedLogs.map((item) => (
                    <tr key={item.audit_id || item.id}>
                      <td data-label="Date / Time">
                        {formatDateTime(item.created_at)}
                      </td>

                      <td data-label="Action">
                        <span className={getActionClass(item.action)}>
                          {item.action || "-"}
                        </span>
                      </td>

                      <td data-label="Module">{item.module || "-"}</td>

                      <td data-label="Record ID">{item.record_id || "-"}</td>

                      <td data-label="Description">
                        {item.description || "-"}
                      </td>

                      <td data-label="User">
                        {item.created_by_name ||
                          item.name ||
                          item.created_by ||
                          "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="performance-pagination">
            <div>
              Showing {paginationStart} to {paginationEnd} of{" "}
              {sortedLogs.length} entries
            </div>

            <div className="performance-pagination-actions">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                First
              </button>

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                Prev
              </button>

              <span>
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
              >
                Next
              </button>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </button>
            </div>
          </div>
        </section>
      </div>
    </TmisLayout>
  );
};

export default AuditLogs;